import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Link,
  Select,
  Text,
  Textarea,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { useMemo, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import MarketingPageShell, {
  breadcrumbSchema,
} from '../components/MarketingPageShell';
import { SITE_URL } from '../components/PageMeta';
import { WW_API_BASE_URL } from '../config/api';

export const CLAIM_EMAIL = 'claim@wagerwatchdog.com';

const BREADCRUMBS = [
  { name: 'Home', path: '/' },
  { name: 'Claim Your Profile', path: '/claim' },
];

const DESCRIPTION =
  'Claim your WagerWatchdog expert profile. Tell us who you are and how to verify you — we will connect your brand to your tracked record.';

function firstParam(params: URLSearchParams, keys: string[]): string {
  for (const key of keys) {
    const value = params.get(key)?.trim();
    if (value) return value;
  }
  return '';
}

function normalizeSource(raw: string): string {
  const key = raw.trim().toLowerCase();
  if (!key) return '';
  if (key.includes('discord')) return 'Discord';
  if (key.includes('twitter') || key === 'x') return 'X / Twitter';
  if (key.includes('telegram')) return 'Telegram';
  return raw.trim();
}

function claimApiUrl(): string {
  const base = WW_API_BASE_URL.replace(/\/$/, '');
  return `${base}/api/v1/public/marketing/claim-profile`;
}

/** Marketing claim-profile form → Mailgun email to claim@wagerwatchdog.com */
export default function ClaimProfile() {
  const [searchParams] = useSearchParams();
  const toast = useToast();

  const initial = useMemo(
    () => ({
      expertName: firstParam(searchParams, [
        'expert',
        'name',
        'author',
        'author_name',
      ]),
      platform: normalizeSource(
        firstParam(searchParams, ['source', 'platform', 'author_source']),
      ),
      profileUrl: firstParam(searchParams, [
        'url',
        'profile',
        'profile_url',
        'href',
      ]),
    }),
    [searchParams],
  );

  const [expertName, setExpertName] = useState(initial.expertName);
  const [platform, setPlatform] = useState(initial.platform);
  const [profileUrl, setProfileUrl] = useState(initial.profileUrl);
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [proof, setProof] = useState('');
  const [notes, setNotes] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!expertName.trim()) {
      toast({
        title: 'Expert name required',
        description: 'Tell us which WagerWatchdog profile you want to claim.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    if (!contactEmail.trim()) {
      toast({
        title: 'Email required',
        description: 'We need an email so we can reply about your claim.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(claimApiUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          expert_name: expertName.trim(),
          platform: platform.trim() || null,
          profile_url: profileUrl.trim() || null,
          contact_name: contactName.trim() || null,
          contact_email: contactEmail.trim(),
          proof: proof.trim() || null,
          notes: notes.trim() || null,
          website: honeypot || null,
        }),
      });
      const payload = (await response.json().catch(() => null)) as {
        message?: string;
        detail?: string | { msg?: string }[];
      } | null;

      if (!response.ok) {
        const detail = payload?.detail;
        const detailText =
          typeof detail === 'string'
            ? detail
            : Array.isArray(detail)
              ? detail.map((d) => d.msg).filter(Boolean).join(' ')
              : null;
        throw new Error(
          detailText ||
            `Could not send your claim (${response.status}). Email ${CLAIM_EMAIL} instead.`,
        );
      }

      setSubmitted(true);
      toast({
        title: 'Claim request sent',
        description:
          payload?.message ||
          `We emailed ${CLAIM_EMAIL}. We will follow up at ${contactEmail.trim()}.`,
        status: 'success',
        duration: 6000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Send failed',
        description:
          error instanceof Error
            ? error.message
            : `Please email ${CLAIM_EMAIL} directly.`,
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <MarketingPageShell
      fullTitle="Claim Your Profile | WagerWatchdog"
      description={DESCRIPTION}
      breadcrumbs={BREADCRUMBS}
      h1="Claim Your"
      h1Accent="Profile"
      lead="Connect your brand to your independently tracked record. Submit the form and we will email our claims team — Reply-To is your address so we can follow up."
      schema={[
        {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'Claim Your Profile | WagerWatchdog',
          description: DESCRIPTION,
          url: `${SITE_URL}/claim`,
        },
        breadcrumbSchema(BREADCRUMBS),
      ]}
    >
      <Box
        as="form"
        onSubmit={onSubmit}
        maxW="640px"
        p={{ base: 4, md: 6 }}
        borderRadius="16px"
        borderWidth="1px"
        borderColor="whiteAlpha.120"
        bg="gray.800"
        position="relative"
      >
        <Text fontSize="sm" color="gray.400" mb={5} lineHeight="1.6">
          Submissions go to{' '}
          <Link
            href={`mailto:${CLAIM_EMAIL}`}
            color="primary.300"
            fontWeight="semibold"
            _hover={{ color: 'primary.200', textDecoration: 'underline' }}
          >
            {CLAIM_EMAIL}
          </Link>
          . We verify ownership before unlocking profile controls.
        </Text>

        {submitted ? (
          <VStack align="stretch" spacing={4}>
            <Text color="gray.100" fontWeight="semibold">
              Thanks — your claim request was sent.
            </Text>
            <Text color="gray.400" fontSize="sm" lineHeight="1.6">
              We will follow up at{' '}
              <Text as="span" color="gray.200">
                {contactEmail}
              </Text>
              . If you do not hear back in a few business days, email{' '}
              <Link href={`mailto:${CLAIM_EMAIL}`} color="primary.300">
                {CLAIM_EMAIL}
              </Link>
              .
            </Text>
            <Button
              type="button"
              variant="outline"
              onClick={() => setSubmitted(false)}
              borderColor="whiteAlpha.300"
              color="gray.200"
              alignSelf="flex-start"
            >
              Submit another request
            </Button>
          </VStack>
        ) : (
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel color="gray.200">
                Expert name on WagerWatchdog
              </FormLabel>
              <Input
                value={expertName}
                onChange={(e) => setExpertName(e.target.value)}
                placeholder="e.g. denimdannn"
                bg="gray.900"
                borderColor="whiteAlpha.200"
              />
              <FormHelperText color="gray.500">
                Use the exact display name from the ratings leaderboard when you
                can.
              </FormHelperText>
            </FormControl>

            <FormControl>
              <FormLabel color="gray.200">Platform</FormLabel>
              <Select
                value={platform}
                onChange={(e) => setPlatform(e.target.value)}
                placeholder="Select platform"
                bg="gray.900"
                borderColor="whiteAlpha.200"
              >
                <option value="Discord">Discord</option>
                <option value="X / Twitter">X / Twitter</option>
                <option value="Telegram">Telegram</option>
                <option value="Other">Other</option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel color="gray.200">Profile or handle URL</FormLabel>
              <Input
                value={profileUrl}
                onChange={(e) => setProfileUrl(e.target.value)}
                placeholder="https://x.com/… or Discord invite / server"
                bg="gray.900"
                borderColor="whiteAlpha.200"
              />
            </FormControl>

            <FormControl>
              <FormLabel color="gray.200">Your name</FormLabel>
              <Input
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                placeholder="How should we address you?"
                bg="gray.900"
                borderColor="whiteAlpha.200"
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel color="gray.200">Your email</FormLabel>
              <Input
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                placeholder="you@example.com"
                bg="gray.900"
                borderColor="whiteAlpha.200"
              />
            </FormControl>

            <FormControl>
              <FormLabel color="gray.200">How can we verify you?</FormLabel>
              <Textarea
                value={proof}
                onChange={(e) => setProof(e.target.value)}
                placeholder="e.g. I can post a verification code from my X account / Discord admin role / reply from the listed handle"
                rows={4}
                bg="gray.900"
                borderColor="whiteAlpha.200"
              />
            </FormControl>

            <FormControl>
              <FormLabel color="gray.200">Additional notes</FormLabel>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Anything else we should know"
                rows={3}
                bg="gray.900"
                borderColor="whiteAlpha.200"
              />
            </FormControl>

            {/* Honeypot — hidden from users */}
            <Box
              aria-hidden
              position="absolute"
              left="-10000px"
              top="auto"
              w="1px"
              h="1px"
              overflow="hidden"
            >
              <FormLabel htmlFor="claim-website">Website</FormLabel>
              <Input
                id="claim-website"
                tabIndex={-1}
                autoComplete="off"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
              />
            </Box>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              mt={2}
              isLoading={isSubmitting}
              loadingText="Sending…"
            >
              Submit
            </Button>
          </VStack>
        )}
      </Box>
    </MarketingPageShell>
  );
}

/** Build `/claim?...` with optional expert context for deep links. */
export function claimProfilePath(opts?: {
  expert?: string | null;
  source?: string | null;
  url?: string | null;
}): string {
  const params = new URLSearchParams();
  if (opts?.expert?.trim()) params.set('expert', opts.expert.trim());
  if (opts?.source?.trim()) params.set('source', opts.source.trim());
  if (opts?.url?.trim()) params.set('url', opts.url.trim());
  const qs = params.toString();
  return qs ? `/claim?${qs}` : '/claim';
}
